import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import PageHeader from '../../components/common/PageHeader';
import { ShieldAlert, Save, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  preventInvalidNumberInput,
  preventInvalidNumberPaste,
} from '../../utils/validationMessages';

type RiskSettings = {
  maxDailyLoss: string;
  maxWeeklyLoss: string;
  maxMonthlyLoss: string;
  maxDrawdown: string;
  maxLotSize: string;
  maxExposure: string;
  symbolRestrictions: string;
};

const initialRiskSettings: RiskSettings = {
  maxDailyLoss: '',
  maxWeeklyLoss: '',
  maxMonthlyLoss: '',
  maxDrawdown: '',
  maxLotSize: '',
  maxExposure: '',
  symbolRestrictions: '',
};

const RiskManagement = () => {
  const [settings, setSettings] = useState<RiskSettings>(initialRiskSettings);
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (key: keyof RiskSettings, value: string) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    await new Promise(r => setTimeout(r, 600));
    setIsSaving(false);
    toast.success('Global copy trading risk settings saved successfully!');
  };

  const handleReset = () => {
    setSettings(initialRiskSettings);
    toast.success('Reset to default risk parameters');
  };

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Copy Trading Risk Management"
          description="Configure account-level drawdown safety guards, position caps, and symbol restrictions across all copy subscriptions"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Global Risk Guard Parameters</CardTitle>
            <CardDescription>
              Enforce strict safety ceilings across all master traders you copy
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-6 text-sm">
              {/* Loss Ceilings */}
              <div className="space-y-3">
                <h3 className="font-semibold text-base flex items-center gap-2 border-b pb-2">
                  <ShieldAlert className="h-5 w-5 text-red-600" />
                  Loss Limits
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="maxDailyLoss">Max Daily Loss ($)</Label>
                    <Input
                      id="maxDailyLoss"
                      type="number"
                      value={settings.maxDailyLoss}
                      onKeyDown={preventInvalidNumberInput}
                      onPaste={preventInvalidNumberPaste}
                      onChange={e =>
                        handleChange('maxDailyLoss', e.target.value)
                      }
                      placeholder="200"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Pause copying if daily loss hits threshold
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="maxWeeklyLoss">Max Weekly Loss ($)</Label>
                    <Input
                      id="maxWeeklyLoss"
                      type="number"
                      value={settings.maxWeeklyLoss}
                      onKeyDown={preventInvalidNumberInput}
                      onPaste={preventInvalidNumberPaste}
                      onChange={e =>
                        handleChange('maxWeeklyLoss', e.target.value)
                      }
                      placeholder="500"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Pause copying if weekly loss hits threshold
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="maxMonthlyLoss">Max Monthly Loss ($)</Label>
                    <Input
                      id="maxMonthlyLoss"
                      type="number"
                      value={settings.maxMonthlyLoss}
                      onKeyDown={preventInvalidNumberInput}
                      onPaste={preventInvalidNumberPaste}
                      onChange={e =>
                        handleChange('maxMonthlyLoss', e.target.value)
                      }
                      placeholder="1500"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Pause copying if monthly loss hits threshold
                    </p>
                  </div>
                </div>
              </div>

              {/* Drawdown & Position Caps */}
              <div className="space-y-3">
                <h3 className="font-semibold text-base border-b pb-2">
                  Drawdown & Exposure Controls
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="maxDrawdown">
                      Max Account Drawdown (%)
                    </Label>
                    <Input
                      id="maxDrawdown"
                      type="number"
                      value={settings.maxDrawdown}
                      onKeyDown={preventInvalidNumberInput}
                      onPaste={preventInvalidNumberPaste}
                      onChange={e =>
                        handleChange('maxDrawdown', e.target.value)
                      }
                      placeholder="25"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Emergency stop if equity drops X%
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="maxLotSize">Max Lot Size per Trade</Label>
                    <Input
                      id="maxLotSize"
                      type="number"
                      step="0.1"
                      value={settings.maxLotSize}
                      onKeyDown={preventInvalidNumberInput}
                      onPaste={preventInvalidNumberPaste}
                      onChange={e => handleChange('maxLotSize', e.target.value)}
                      placeholder="2.0"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Hard cap on individual position size
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="maxExposure">Max Total Exposure ($)</Label>
                    <Input
                      id="maxExposure"
                      type="number"
                      value={settings.maxExposure}
                      onKeyDown={preventInvalidNumberInput}
                      onPaste={preventInvalidNumberPaste}
                      onChange={e =>
                        handleChange('maxExposure', e.target.value)
                      }
                      placeholder="25000"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Maximum total value of open trades
                    </p>
                  </div>
                </div>
              </div>

              {/* Symbol Restrictions */}
              <div className="space-y-2 border-t pt-4">
                <Label htmlFor="symbolRestrictions">
                  Excluded Symbols / Restricted Instruments
                </Label>
                <Input
                  id="symbolRestrictions"
                  value={settings.symbolRestrictions}
                  onChange={e =>
                    handleChange('symbolRestrictions', e.target.value)
                  }
                  placeholder="Comma separated symbols, e.g., BTCUSD, ETHUSD, XAUUSD"
                />
                <p className="text-xs text-muted-foreground">
                  Trades for these instruments will be skipped automatically
                  across all master traders.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  disabled={isSaving}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reset
                </Button>
                <Button type="submit" disabled={isSaving}>
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>Risk Management Rules</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <ul className="space-y-3 list-disc list-inside">
              <li>
                <strong className="text-foreground">
                  Automated Safety Cut:
                </strong>{' '}
                When max daily or drawdown limits are hit, your account
                automatically stops copying new trades.
              </li>
              <li>
                <strong className="text-foreground">Per-Trade Cap:</strong>{' '}
                Individual order lots will be scaled down if they exceed your
                Max Lot Size setting.
              </li>
              <li>
                <strong className="text-foreground">Symbol Exclusion:</strong>{' '}
                Restricted symbols will never be executed on your account,
                regardless of master trader actions.
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RiskManagement;
